import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, ThumbsUp, ThumbsDown, Plus, Crown, Trash, Compass, Lightbulb } from "@phosphor-icons/react";

const STATUS_META = {
  open: { label: "Open", cls: "border-[#D8D3C8] text-[#525252] bg-[#F5F3ED]" },
  planned: { label: "Planned", cls: "border-[#BFDBFE] text-[#1E40AF] bg-[#EFF6FF]" },
  building: { label: "Building", cls: "border-[#FDE68A] text-[#92400E] bg-[#FEF3C7]" },
  shipped: { label: "Shipped", cls: "border-[#A7F3D0] text-[#047857] bg-[#ECFDF5]" },
  declined: { label: "Not planned", cls: "border-[#E5E5E5] text-[#8A867D] bg-white" },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.open;
  return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${m.cls}`}>{m.label}</span>;
}

export default function Roadmap() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [statuses, setStatuses] = useState(["open", "planned", "building", "shipped", "declined"]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/feature-requests");
      setItems(data.items || []);
      setIsAdmin(!!data.is_admin);
      if (data.statuses) setStatuses(data.statuses);
    } catch (e) {
      toast.error("Couldn't load the roadmap");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Give your idea a short title."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/feature-requests", { title: title.trim(), description: desc.trim() });
      setItems((prev) => [data, ...prev]);
      setTitle(""); setDesc(""); setShowForm(false);
      toast.success("Thanks — your idea is live and already has your vote. 🙌");
    } catch (err) {
      toast.error("Couldn't post your idea — please try again");
    } finally {
      setBusy(false);
    }
  };

  const vote = async (id, direction) => {
    try {
      const { data } = await api.post(`/feature-requests/${id}/vote`, { direction });
      setItems((prev) => prev.map((x) => (x.id === id ? data : x)));
    } catch (e) {
      toast.error("Couldn't record your vote");
    }
  };

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/feature-requests/${id}`, { status });
      setItems((prev) => prev.map((x) => (x.id === id ? data : x)));
      toast.success(`Marked "${data.title}" as ${STATUS_META[status]?.label || status}.`);
    } catch (e) {
      toast.error("Couldn't update status");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this idea?")) return;
    try {
      await api.delete(`/feature-requests/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success("Idea removed.");
    } catch (e) {
      toast.error("Couldn't remove that");
    }
  };

  const counts = useMemo(() => {
    const c = { all: items.length };
    for (const s of statuses) c[s] = 0;
    for (const it of items) c[it.status] = (c[it.status] || 0) + 1;
    return c;
  }, [items, statuses]);

  const filtered = filter === "all" ? items : items.filter((x) => x.status === filter);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="roadmap-page">
      <header className="sticky top-0 z-30 border-b border-[#D8D3C8] bg-[#FDFBF7]/90 backdrop-blur">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/vv_logo.jpg" alt="Vintage Vantage" onClick={() => navigate("/dashboard")} className="h-9 w-9 rounded-full object-cover ring-1 ring-[#E5E0D6] cursor-pointer shrink-0 hover:ring-[#B47A3C] transition-all" data-testid="app-compass-mark" title="Vintage Vantage — home" />
            <button onClick={() => navigate("/dashboard")} data-testid="roadmap-back-button" className="inline-flex items-center gap-2 font-medium hover:text-[#B47A3C] transition-colors">
              <ArrowLeft size={18} weight="bold" /> Dashboard
            </button>
          </div>
          <span className="text-sm font-semibold text-[#525252] truncate max-w-[200px]">{user?.name || user?.email}</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex items-center gap-2 mb-2"><Compass size={22} weight="fill" className="text-[#B47A3C]" /> <span className="label-caps">The Founding Council steers this</span></div>
        <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-none font-medium mb-3">Shape the roadmap</h1>
        <p className="text-lg text-[#525252] leading-relaxed max-w-2xl mb-6">
          Suggest what would save you the most time or uncertainty — and upvote the ideas that matter to you. The
          most-wanted rise to the top and tell us exactly what to build next.
        </p>

        {/* Under exploration — next platform candidates */}
        <div className="border border-[#0D1B2A]/20 bg-white p-5 mb-8 flex flex-wrap items-center gap-x-4 gap-y-3" data-testid="roadmap-next-platforms">
          <span className="label-caps text-[#B47A3C]">Platforms under exploration</span>
          <div className="flex flex-wrap gap-2">
            {["eBay", "Etsy", "EBTH", "Direct-to-consumer"].map((p) => (
              <span key={p} className="inline-flex items-center h-8 px-3 border border-[#0D1B2A]/25 bg-[#FDFBF7] text-sm font-semibold text-[#0D1B2A]">{p}</span>
            ))}
          </div>
          <span className="text-sm text-[#8A867D]">No dates, no promises — your votes and ideas below steer the priority.</span>
        </div>

        {/* Suggest */}
        <div className="mb-8">
          {!showForm ? (
            <button onClick={() => setShowForm(true)} data-testid="roadmap-suggest-open" className="h-12 px-5 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium inline-flex items-center gap-2 hover:bg-black transition-transform active:scale-[0.98]">
              <Lightbulb size={18} weight="fill" /> Suggest a feature
            </button>
          ) : (
            <form onSubmit={submit} className="border-2 border-[#0D1B2A] bg-white p-6" data-testid="roadmap-suggest-form">
              <label className="label-caps block mb-1.5">Your idea</label>
              <input data-testid="roadmap-title-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140} placeholder="e.g. Auto-detect the designer from a photo" className="h-12 w-full border border-[#0D1B2A]/30 rounded-none px-3 text-[16px] focus:border-[#0D1B2A] outline-none mb-3" />
              <label className="label-caps block mb-1.5">A little more <span className="text-[#8A867D] normal-case font-normal">(optional)</span></label>
              <textarea data-testid="roadmap-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} maxLength={1000} placeholder="What problem would it solve for you?" className="w-full border border-[#0D1B2A]/30 rounded-none px-3 py-2.5 text-[15px] focus:border-[#0D1B2A] outline-none resize-none mb-4" />
              <div className="flex items-center gap-2">
                <button type="submit" disabled={busy} data-testid="roadmap-submit" className="h-12 px-5 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium inline-flex items-center gap-2 hover:bg-black transition-transform active:scale-[0.98] disabled:opacity-60">
                  {busy ? "Posting…" : "Post idea"} <Plus size={16} weight="bold" />
                </button>
                <button type="button" onClick={() => { setShowForm(false); setTitle(""); setDesc(""); }} className="h-12 px-4 border border-[#0D1B2A] rounded-none font-medium hover:bg-[#F5F3ED] transition-colors">Cancel</button>
              </div>
            </form>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="roadmap-filters">
          {[["all", "All"], ...statuses.map((s) => [s, STATUS_META[s]?.label || s])].map(([key, label]) => (
            <button key={key} onClick={() => setFilter(key)} data-testid={`roadmap-filter-${key}`} className={`text-sm font-semibold px-3 py-1.5 rounded-full border transition-colors ${filter === key ? "bg-[#0D1B2A] text-white border-[#0D1B2A]" : "bg-white text-[#525252] border-[#D8D3C8] hover:border-[#0D1B2A]"}`}>
              {label} <span className={filter === key ? "text-white/70" : "text-[#8A867D]"}>{counts[key] || 0}</span>
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="p-10 text-center text-[#8A867D]">Loading ideas…</div>
        ) : filtered.length === 0 ? (
          <div className="border border-[#0D1B2A]/20 bg-white p-10 text-center text-[#525252]" data-testid="roadmap-empty">No ideas here yet — be the first to suggest one.</div>
        ) : (
          <div className="space-y-3" data-testid="roadmap-list">
            {filtered.map((it, i) => (
              <div key={it.id} data-testid={`roadmap-card-${i}`} className="border border-[#0D1B2A]/20 bg-white p-4 sm:p-5 flex gap-4">
                <div className="shrink-0 w-14 flex flex-col items-center gap-1" data-testid={`roadmap-vote-${i}`}>
                  <button
                    onClick={() => vote(it.id, "up")}
                    data-testid={`roadmap-upvote-${i}`}
                    className={`h-9 w-full flex items-center justify-center border rounded-none transition-colors ${it.has_voted ? "bg-[#0D1B2A] text-white border-[#0D1B2A]" : "bg-white text-[#0D1B2A] border-[#D8D3C8] hover:border-[#0D1B2A]"}`}
                    title="Yes — build this"
                  >
                    <ThumbsUp size={16} weight={it.has_voted ? "fill" : "regular"} />
                  </button>
                  <span className="font-serif-display text-xl font-bold leading-none" data-testid={`roadmap-votecount-${i}`}>{it.vote_count}</span>
                  <button
                    onClick={() => vote(it.id, "down")}
                    data-testid={`roadmap-downvote-${i}`}
                    className={`h-9 w-full flex items-center justify-center border rounded-none transition-colors ${it.has_downvoted ? "bg-[#B47A3C] text-white border-[#B47A3C]" : "bg-white text-[#8A867D] border-[#D8D3C8] hover:border-[#B47A3C]"}`}
                    title="Not for me"
                  >
                    <ThumbsDown size={16} weight={it.has_downvoted ? "fill" : "regular"} />
                  </button>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-serif-display text-xl font-bold leading-tight">{it.title}</h3>
                    <StatusBadge status={it.status} />
                  </div>
                  {it.description && <p className="text-[15px] text-[#525252] leading-relaxed mb-2">{it.description}</p>}
                  <div className="flex flex-wrap items-center gap-2 text-sm text-[#8A867D]">
                    <span className="inline-flex items-center gap-1">
                      {it.author_is_founding && <Crown size={13} weight="fill" className="text-[#B47A3C]" />}
                      {it.author_name}
                    </span>
                    {(isAdmin || it.is_mine) && (
                      <button onClick={() => remove(it.id)} data-testid={`roadmap-delete-${i}`} className="inline-flex items-center gap-1 text-[#8A867D] hover:text-[#B47A3C] transition-colors ml-1">
                        <Trash size={13} /> Remove
                      </button>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="mt-3">
                      <label className="label-caps mr-2">Status</label>
                      <select
                        value={it.status}
                        onChange={(e) => setStatus(it.id, e.target.value)}
                        data-testid={`roadmap-status-select-${i}`}
                        className="h-9 border border-[#D8D3C8] rounded-none px-2 text-sm focus:border-[#0D1B2A] outline-none"
                      >
                        {statuses.map((s) => <option key={s} value={s}>{STATUS_META[s]?.label || s}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
